import React, { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { carbonApi } from '../../api/carbon';
import {
  GFEmblem,
  GFHome,
  GFUsers,
  GFPackage,
  GFDoc,
  GFWallet,
  GFStock,
  GFBar,
  GFDownload,
  GFLeaf,
  GFSettings,
  GFChevronDown,
  GFPlus,
  GFCalendar,
} from '../icons/GFIcons';
import { APP_VERSION } from '../../version';

interface NavLeaf {
  id: string;
  label: string;
  path: string;
  icon: React.FC<{ size?: number; className?: string }>;
  badge?: string;
  badgeWarn?: boolean;
  tag?: string;
  external?: () => void;
}

interface NavGroup {
  label: string;
  items: NavLeaf[];
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  collapsed?: boolean;
}

export function Sidebar({ isOpen, onClose, collapsed = false }: SidebarProps) {
  const { t } = useLanguage();
  const { user, companies, activeCompanyId } = useAuth();
  const location = useLocation();

  const activeCompany = companies?.find((c) => c.id === activeCompanyId);

  const openGtsCarbon = async () => {
    // Close the mobile drawer before leaving so it doesn't sit on top of the
    // page during the redirect.
    onClose();
    try {
      // returnPath is a GTS Carbon route, not a TuniFact one — passing our own
      // location.pathname (e.g. /factures, /clients) would land the user on a
      // path GTS doesn't know and bounce them through its wildcard to the
      // dashboard anyway. Send GTS's own dashboard explicitly, matching the
      // CarbonFootprintWidget entry point.
      const { redirectUrl } = await carbonApi.startSso('/dashboard');
      window.location.href = redirectUrl;
    } catch {
      window.alert("GTS Carbon indisponible — vérifiez votre plan ou la connexion.");
    }
  };

  const groups: NavGroup[] = [
    {
      label: 'Pilotage',
      items: [
        { id: 'dashboard', label: t.nav.dashboard, path: '/', icon: GFHome },
        { id: 'cockpit', label: 'Cockpit dirigeant', path: '/dashboard/dirigeant', icon: GFBar },
        { id: 'alertes', label: 'Alertes', path: '/alertes', icon: GFBar },
        { id: 'tresorerie', label: 'Trésorerie', path: '/tresorerie', icon: GFWallet },
        { id: 'cockpit-carbone', label: 'Cockpit carbone', path: '/carbone/cockpit', icon: GFBar },
        { id: 'gouvernance-carbone', label: 'Gouvernance carbone', path: '/carbone/gouvernance', icon: GFLeaf },
        { id: 'analytique', label: 'Analytique', path: '/analytique', icon: GFBar },
        { id: 'qualite-donnees', label: 'Qualité des données', path: '/qualite-donnees', icon: GFBar },
        { id: 'validations', label: 'Validations', path: '/validations', icon: GFBar },
        { id: 'esg', label: 'Déclaration ESG', path: '/analytique/esg', icon: GFLeaf, tag: 'GTS' },
      ],
    },
    {
      label: 'Ventes',
      items: [
        { id: 'clients', label: t.nav.clients, path: '/clients', icon: GFUsers },
        { id: 'pipeline', label: 'Pipeline', path: '/crm/pipeline', icon: GFBar },
        { id: 'assistant-devis', label: 'Assistant devis', path: '/assistant/devis', icon: GFBar },
        { id: 'devis', label: t.nav.devis, path: '/documents/devis', icon: GFDoc },
        { id: 'commandes', label: 'Commandes', path: '/documents/commandes', icon: GFDoc },
        { id: 'factures', label: t.nav.factures, path: '/documents/factures', icon: GFDoc },
        { id: 'avoirs', label: t.nav.avoirs, path: '/documents/avoirs', icon: GFDoc },
        { id: 'bl', label: t.nav.bonsLivraison, path: '/documents/bons-livraison', icon: GFDoc },
        { id: 'paiements', label: t.nav.paiements, path: '/paiements', icon: GFWallet },
        { id: 'impayes', label: 'Impayés', path: '/recouvrement/impayes', icon: GFWallet },
        { id: 'relances-config', label: 'Relances & promesses', path: '/recouvrement/relances', icon: GFWallet },
      ],
    },
    {
      label: 'Achats',
      items: [
        { id: 'fournisseurs', label: 'Fournisseurs', path: '/fournisseurs', icon: GFUsers },
        { id: 'bons-commande', label: 'Bons de commande', path: '/documents/bons-commande', icon: GFDoc },
        { id: 'factures-achat', label: 'Factures fournisseurs', path: '/documents/factures-achat', icon: GFDoc },
        { id: 'achats-new', label: 'Nouvelle facture achat', path: '/stock/achats/new', icon: GFPlus },
        { id: 'depots-factures', label: 'Dépôts fournisseurs', path: '/achats/depots-factures', icon: GFDoc },
      ],
    },
    {
      label: 'Catalogue & Stock',
      items: [
        { id: 'articles', label: t.nav.articles, path: '/articles', icon: GFPackage },
        { id: 'stock', label: 'Mouvements de stock', path: '/stock/mouvements', icon: GFStock },
        { id: 'stock-depots', label: 'Dépôts & lots', path: '/stock/depots', icon: GFStock },
        { id: 'stock-analytique', label: 'Analytique stock', path: '/stock/analytique', icon: GFBar },
        { id: 'stock-valorisation', label: 'Valorisation stock', path: '/stock/valorisation', icon: GFBar },
        { id: 'reappro', label: 'Réappro intelligent', path: '/stock/reappro', icon: GFStock },
      ],
    },
    {
      label: 'Comptabilité',
      items: [
        { id: 'compta', label: 'Vue d’ensemble', path: '/comptabilite', icon: GFBar },
        { id: 'journal', label: 'Journal des écritures', path: '/comptabilite/journal', icon: GFDoc },
        { id: 'rappro-assiste', label: 'Rapprochement assisté', path: '/comptabilite/rapprochement-assiste', icon: GFDoc },
        { id: 'charges', label: 'Charges & dépenses', path: '/comptabilite/charges', icon: GFDoc },
        { id: 'grand-livre', label: 'Grand-livre', path: '/comptabilite/grand-livre', icon: GFDoc },
        { id: 'balance', label: 'Balance', path: '/comptabilite/balance', icon: GFBar },
        { id: 'bilan', label: 'Bilan', path: '/comptabilite/bilan', icon: GFBar },
        { id: 'etat-resultat', label: 'État de résultat', path: '/comptabilite/etat-resultat', icon: GFBar },
        { id: 'plan-comptable', label: 'Plan comptable', path: '/comptabilite/plan-comptable', icon: GFDoc },
        { id: 'tva', label: t.nav.rapportTva, path: '/rapports/tva', icon: GFBar },
        { id: 'controle-fiscal', label: 'Contrôle fiscal', path: '/fiscalite/controle', icon: GFBar },
        { id: 'marge', label: 'Rapport de marge', path: '/rapports/marge', icon: GFBar },
        { id: 'etat-annuel', label: t.nav.etatAnnuel, path: '/rapports/etat-annuel', icon: GFBar },
        { id: 'export', label: 'Export comptable', path: '/rapports/export', icon: GFDownload },
      ],
    },
    {
      label: 'Ressources humaines',
      items: [
        { id: 'rh', label: 'Vue d’ensemble', path: '/rh', icon: GFUsers },
        { id: 'rh-employes', label: 'Salariés', path: '/rh/employes', icon: GFUsers },
        { id: 'rh-conges', label: 'Congés', path: '/rh/conges', icon: GFCalendar },
        { id: 'rh-paie', label: 'Paie', path: '/rh/paie', icon: GFWallet },
      ],
    },
    {
      label: 'Plateforme',
      items: [
        { id: 'parametres', label: t.nav.parametres, path: '/parametres', icon: GFSettings },
      ],
    },
  ];

  // Sélection de l'élément actif : on retient l'item dont le chemin est le plus
  // long préfixe du chemin courant. Évite que « Factures » et « Factures
  // fournisseurs » (ou « Comptabilité » et ses sous-pages) s'allument ensemble.
  const activePath = useMemo(() => {
    const p = location.pathname;
    let best = '';
    for (const group of groups) {
      for (const it of group.items) {
        if (it.path === '/') continue;
        if (p === it.path || p.startsWith(it.path + '/')) {
          if (it.path.length > best.length) best = it.path;
        }
      }
    }
    return best;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const initials = user ? `${user.prenom?.[0] ?? ''}${user.nom?.[0] ?? ''}`.toUpperCase() || 'GF' : 'GF';
  const userName = user ? `${user.prenom} ${user.nom}` : 'Utilisateur';
  const userEmail = user?.email || '';

  return (
    <>
      {isOpen && (
        <div className="sidebar-backdrop lg:hidden" onClick={onClose} />
      )}

      <aside
        className={`sidebar thin-scroll ${isOpen ? 'mobile-open' : ''} ${collapsed ? 'collapsed' : ''}`}
      >
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <GFEmblem size={14} />
          </div>
          {!collapsed && (
            <>
              <div className="sidebar-brand">
                <div className="sidebar-brand-name">GreenBill</div>
                <div className="sidebar-brand-org">
                  {activeCompany?.raisonSociale?.toUpperCase() || 'WORKSPACE'}
                </div>
              </div>
              {companies && companies.length > 1 && (
                <button
                  className="icon-btn"
                  style={{ marginLeft: 'auto', width: 24, height: 24 }}
                  title="Changer de société"
                  type="button"
                >
                  <GFChevronDown size={12} />
                </button>
              )}
            </>
          )}
        </div>

        <nav className="sidebar-nav thin-scroll">
          {groups.map((group, gi) => (
            <div key={gi}>
              {!collapsed && <div className="sidebar-section-label">{group.label}</div>}
              {collapsed && gi > 0 && (
                <div style={{ height: 1, background: 'var(--rule)', margin: '8px 6px' }} />
              )}
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive =
                  item.path === '/'
                    ? location.pathname === '/'
                    : item.path === activePath;
                return (
                  <NavLink
                    key={item.id}
                    to={item.path}
                    onClick={onClose}
                    className={() => `nav-item ${isActive ? 'active' : ''}`}
                    title={collapsed ? item.label : undefined}
                    end={item.path === '/'}
                  >
                    <Icon size={16} />
                    <span className="nav-label">{item.label}</span>
                    {item.tag && <span className="nav-tag">{item.tag}</span>}
                    {item.badge && (
                      <span className={`nav-badge ${item.badgeWarn ? 'warn' : ''}`}>
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}

          <div>
            {!collapsed && <div className="sidebar-section-label">Carbone</div>}
            <button
              type="button"
              onClick={openGtsCarbon}
              className="nav-item"
              title={collapsed ? 'GTS Carbon' : undefined}
              style={{ color: 'var(--brand-green)' }}
            >
              <GFLeaf size={16} />
              <span className="nav-label">Empreinte carbone</span>
              {!collapsed && <span className="nav-tag">GTS</span>}
            </button>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-card" title={collapsed ? userName : undefined}>
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <div className="user-name">{userName}</div>
              <div className="user-org">{userEmail}</div>
            </div>
            <GFChevronDown size={12} className="user-chevron" />
          </div>
          {!collapsed && (
            <div className="sidebar-version" title={`TuniFact v${APP_VERSION}`}>
              v{APP_VERSION}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
